import React, { useCallback, useMemo } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  TextField, 
  Button,
  Divider,
  Paper,
  List,
  ListItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InvoiceNotesTabStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceNotesTabStyles';
import useInvoiceNotes from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceNotes';
import DeleteNoteConfirmationDialog from './DeleteNoteConfirmationDialog';

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  if (extension === 'pdf') return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return <ImageIcon sx={{ color: '#1976d2' }} />;
  return <DescriptionIcon sx={{ color: '#757575' }} />;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const InvoiceNotesTab = ({ invoice, onSaveNotes, onNavigateToFile, onNavigateToSpecificFile }) => {
  const { 
    notes, 
    setNotes, 
    isSaving, 
    isDeleting, 
    handleSave, 
    openDeleteConfirmation, 
    showDeleteConfirmation,
    closeDeleteConfirmation,
    confirmDeleteNote,
    previousNotes,
    selectedFiles,
    isUploadingFiles,
    uploadError,
    handleFileSelect,
    handleRemoveFile
  } = useInvoiceNotes(invoice, onSaveNotes);

  const handleAttachmentClick = useCallback((attachment) => {
    if (onNavigateToSpecificFile) {
      onNavigateToSpecificFile(attachment.fileName);
    } else if (onNavigateToFile) {
      onNavigateToFile();
    }
    // Commenting out automatic opening in new tab to allow in-app navigation
    // if (attachment.downloadUrl) {
    //   window.open(attachment.downloadUrl, '_blank');
    // }
  }, [onNavigateToFile, onNavigateToSpecificFile]);

  const processedNotes = useMemo(() => {
    return previousNotes.map((note, index) => ({
      ...note,
      uniqueId: note.timestamp || `note-${index}`,
      isAttachmentsOnly: note.isAttachmentOnly || 
                        ((!note.text || note.text.trim() === '') && note.attachments && note.attachments.length > 0) ||
                        (note.text && note.text.startsWith('[צרופות:') && note.text.endsWith(']'))
    }));
  }, [previousNotes]);

  const sortedNotes = useMemo(() => [...processedNotes].reverse(), [processedNotes]);

  const RegularNoteDisplay = useCallback(({ note, index }) => (
    <Box sx={{ position: 'relative' }}>
      <Box sx={InvoiceNotesTabStyles.noteHeader}>
        <Box sx={InvoiceNotesTabStyles.userInfoContainer}>
          <Avatar sx={InvoiceNotesTabStyles.userAvatar}>
            {getInitials(note.user)}
          </Avatar>
          <Typography variant="subtitle2" sx={InvoiceNotesTabStyles.noteUser}>
            {note.user || 'לא ידוע'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={InvoiceNotesTabStyles.noteDate}>
            {formatDate(note.timestamp)}
          </Typography>
          <Tooltip title="מחק הערה">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => openDeleteConfirmation(processedNotes.length - 1 - index)}
              disabled={isDeleting}
              sx={{ 
                opacity: 0.7, 
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.2s'
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={InvoiceNotesTabStyles.noteContent}>
        {note.text && note.text.trim() && !note.isAttachmentsOnly && (
          <Typography variant="body2" sx={InvoiceNotesTabStyles.noteText}>
            {note.text.split('\n').map((line, lineIndex) => {
              const isEmailHeader = line.trim() === 'חשבונית זו הועלתה דרך המייל' || line.trim() === 'תוכן המייל:';
              return (
                <React.Fragment key={lineIndex}>
                  {isEmailHeader ? (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontWeight: 700,
                        color: '#1976d2',
                        fontSize: '1.05em',
                        display: 'block',
                        marginBottom: '8px'
                      }}
                    >
                      {line}
                    </Box>
                  ) : (
                    line
                  )}
                  {lineIndex < note.text.split('\n').length - 1 && <br />}
                </React.Fragment>
              );
            })}
          </Typography>
        )}
        {note.attachments && note.attachments.length > 0 && (
          <Box sx={{ mt: note.text && note.text.trim() && !note.isAttachmentsOnly ? 2 : 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {note.attachments.map((attachment, attachIndex) => (
                <Tooltip 
                  key={`${note.uniqueId}-regular-attachment-${attachIndex}`}
                  title="לחץ כדי לעבור לטאב הקבצים ולצפות בצרופה"
                  arrow
                >
                  <Chip
                    icon={<FolderOpenIcon fontSize="small" />}
                    label={attachment.fileName}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAttachmentClick(attachment)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  ), [handleAttachmentClick, openDeleteConfirmation, isDeleting, processedNotes.length]);

  return (
    <Box sx={InvoiceNotesTabStyles.container}>
      <Card sx={InvoiceNotesTabStyles.card}>
        <Typography variant="h6" gutterBottom color="primary" sx={InvoiceNotesTabStyles.mainTitle}>
          הערות וצרופות לחשבונית
        </Typography>
        
        {sortedNotes.length > 0 && (
          <>
            <Box sx={InvoiceNotesTabStyles.previousNotesHeader}>
              <ChatBubbleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                היסטוריית הערות וצרופות
              </Typography>
              <Chip 
                label={sortedNotes.length} 
                size="small" 
                color="primary" 
                sx={InvoiceNotesTabStyles.noteCountChip} 
              />
            </Box>
            <Paper variant="outlined" sx={InvoiceNotesTabStyles.previousNotesContainer}>
              <List disablePadding>
                {sortedNotes.map((note, index) => (
                  <ListItem 
                    key={`list-item-${note.uniqueId}`}
                    sx={InvoiceNotesTabStyles.previousNoteItem}
                    divider={index !== sortedNotes.length - 1}
                  >
                    <Box sx={InvoiceNotesTabStyles.noteWrapper}>
                      <RegularNoteDisplay note={note} index={index} />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
            <Divider sx={InvoiceNotesTabStyles.sectionDivider} />
          </>
        )}
        
        <Box sx={InvoiceNotesTabStyles.addNoteSection}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={InvoiceNotesTabStyles.newNotesLabel}>
            הוספת הערה חדשה
          </Typography>
          
          <Paper variant="outlined" sx={InvoiceNotesTabStyles.newNoteContainer}>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="הקלד את ההערה שלך כאן..."
              variant="outlined"
              sx={InvoiceNotesTabStyles.textField}
            />
            
            <Box sx={{
              mt: 2,
              p: 2,
              border: '1px dashed #ccc',
              borderRadius: 2,
              backgroundColor: '#fafafa'
            }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                צרופות
              </Typography>
              
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="attachment-upload"
                multiple
                type="file"
                onChange={handleFileSelect}
                disabled={isUploadingFiles}
              />
              <label htmlFor="attachment-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={isUploadingFiles}
                  sx={{ mb: 2 }}
                >
                  בחר קבצים לצרופה
                </Button>
              </label>

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {selectedFiles.length > 0 && (
                <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    קבצים נבחרים ({selectedFiles.length}):
                  </Typography>
                  {selectedFiles.map((file, index) => (
                    <Box key={`selected-file-${index}-${file.name}`} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {getFileIcon(file.name)}
                        <Typography variant="body2" sx={{ ml: 1, mr: 2, fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                        {!file.uploaded && !file.uploading && (
                          <Chip 
                            label="ממתין להעלאה" 
                            color="warning" 
                            variant="outlined"
                            size="small" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(index)}
                        disabled={file.uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            
            <Box sx={InvoiceNotesTabStyles.buttonsContainer}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                disabled={isSaving || isUploadingFiles || (!notes.trim() && selectedFiles.length === 0)}
                sx={InvoiceNotesTabStyles.saveButton}
              >
                {isSaving ? 'שומר...' : 
                 isUploadingFiles ? 'מעלה קבצים...' : 
                 notes.trim() && selectedFiles.length > 0 ? 'שמור הערה וצרופות' :
                 notes.trim() ? 'שמור הערה' : 
                 selectedFiles.length > 0 ? 'שמור צרופות' : 
                 'שמור הערה וצרופות'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Card>
      
      <DeleteNoteConfirmationDialog 
        open={showDeleteConfirmation}
        onClose={closeDeleteConfirmation}
        onConfirm={confirmDeleteNote}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default InvoiceNotesTab;